import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Định nghĩa cấu trúc dữ liệu hồ sơ người dùng
 */
export type ProfileResponseDto = {
  /** ID duy nhất của hồ sơ */
  id: number;
  /** Họ và tên đầy đủ */
  fullName: string;
  /** URL ảnh đại diện (tùy chọn) */
  avatar?: string;
  /** Tiểu sử cá nhân (tùy chọn) */
  bio?: string;
  /** URL website cá nhân (tùy chọn) */
  websiteUrl?: string;
  /** Địa chỉ (tùy chọn) */
  address?: string;
  /** Email */
  email: string;
  /** Số điện thoại (tùy chọn) */
  phone?: string;
  /** Giới tính (tùy chọn) */
  gender?: 'male' | 'female' | 'other';
  /** Ngày sinh (tùy chọn) */
  dateOfBirth?: string;
  /** Thông tin mạng xã hội (tùy chọn) */
  socialMedia?: {
    /** Link Facebook (tùy chọn) */
    facebook?: string;
    /** Link Twitter (tùy chọn) */
    twitter?: string;
    /** Link Instagram (tùy chọn) */
    instagram?: string;
    /** Link LinkedIn (tùy chọn) */
    linkedin?: string;
  };
  /** Trạng thái hoạt động */
  isActive: boolean;
  /** Thời gian tạo */
  createdAt: string;
  /** Thời gian cập nhật */
  updatedAt: string;
  /** ID người dùng */
  userId: number;
};

/**
 * Định nghĩa cấu trúc dữ liệu yêu cầu cập nhật hồ sơ
 * Loại bỏ các trường không thể chỉnh sửa: id, createdAt, updatedAt, userId
 */
export type UpsertProfileRequest = Partial<Omit<ProfileResponseDto, 'id' | 'createdAt' | 'updatedAt' | 'userId'>>;

/**
 * Service quản lý hồ sơ người dùng
 */
export class ProfilesService {
  private static baseUrl = getApiBase();

  /**
   * Lấy headers xác thực cho các request API
   * @returns Object chứa headers xác thực
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  /**
   * Lấy hồ sơ của người dùng hiện tại
   * @returns Promise chứa thông tin hồ sơ hoặc null nếu không tìm thấy
   */
  static async getMyProfile(): Promise<ProfileResponseDto | null> {
    const resp = await apiClient.get('/profiles/me');
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải hồ sơ';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return resp.json();
  }

  /**
   * Cập nhật hoặc tạo mới hồ sơ của người dùng hiện tại
   * @param data - Dữ liệu hồ sơ cần cập nhật
   * @returns Promise chứa thông tin hồ sơ đã được cập nhật
   */
  static async upsertMyProfile(data: UpsertProfileRequest): Promise<ProfileResponseDto> {
    const resp = await apiClient.patch('/profiles/me', data);
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      const message = errorData?.message || 'Cập nhật hồ sơ thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return resp.json();
  }

  /**
   * Tải lên ảnh đại diện cho hồ sơ
   * @param profileId - ID của hồ sơ
   * @param file - File ảnh cần tải lên
   * @returns Promise chứa thông tin hồ sơ đã được cập nhật với ảnh đại diện mới
   */
  static async uploadAvatar(profileId: number, file: File): Promise<ProfileResponseDto> {
    const form = new FormData();
    form.append('avatar', file);
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('access_token');
    if (token) headers.Authorization = `Bearer ${token}`;

    const resp = await apiClient.request(`/profiles/${profileId}/avatar`, {
      method: 'POST',
      headers,
      body: form,
    });
    
    if (!resp.ok) {
      const errorData = await resp.json().catch(() => ({}));
      const message = errorData?.message || 'Tải ảnh đại diện thất bại';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    
    return resp.json();
  }

  /**
   * Lấy thông tin hồ sơ công khai của người dùng theo ID
   * @param userId - ID của người dùng
   * @returns Promise chứa thông tin hồ sơ công khai
   */
  static async getPublicProfile(userId: number): Promise<ProfileResponseDto> {
    const response = await fetch(`${this.baseUrl}/public/profiles/${userId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải thông tin hồ sơ';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    
    return response.json();
  }
}


